import { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { formatCurrency } from '@tmh-website/services/price.client.service';
import { CustomerCartContext } from '@tmh-website/shared';
import { Color } from '@tmh-website/shared/colors';
import { LocalePrefix } from '@tmh-website/shared/const';
import StyledTypo from '@tmh-website/styles/typo';
import Button from '@tmh-website/ui/Button';
import ButtonOutlined from '@tmh-website/ui/ButtonOutlined';

import * as Styled from './MiniCart.styles';
import MiniCartEntry from './MiniCartEntry';

interface CartProps {
  locale: LocalePrefix;
  isStandaloneMode: boolean;
}

const MiniCart = ({ locale, isStandaloneMode }: CartProps) => {
  const { cart } = useContext(CustomerCartContext);
  const { t } = useTranslation();

  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <Styled.Wrapper>
        <Styled.EmptyState>
          <Styled.EmptyStateHeader as='div' $style='h5'>
            {t('global:cart.emptyCartMessage')}
          </Styled.EmptyStateHeader>
          <StyledTypo.Typography as='p' $style='uiText'>
            <Trans t={t} i18nKey='global:cart.haveFunMessage'>
              <b>Viel Spaß beim Einkaufen!</b>
              <br />
              Dein Mobility House Team
            </Trans>
          </StyledTypo.Typography>
          <div>
            {/**
             * //TODO: this URL might differ by language. On the current website, it's coming from `window.checkout.assortmentUrl`,
             * but we don't have access to this property due to it being injected into the old header.
             */}
            <Button hoverThemeColor={Color.Blue_1000_Deep} href={`/${locale}/produkte-services`}>
              {t('global:cart.goToShopButton')}
            </Button>
          </div>
        </Styled.EmptyState>
      </Styled.Wrapper>
    );
  }

  return (
    <Styled.Wrapper>
      <Styled.TitleContainer>
        <div>{t('global:cart.headline')}</div>
        <Styled.GrandTotal>{formatCurrency({ amount: cart.subtotalIncludingTax, locale })}</Styled.GrandTotal>
      </Styled.TitleContainer>
      <Styled.ItemListContainer>
        <Styled.ItemList>
          {cart.items.map(item => (
            <MiniCartEntry
              isStandaloneMode={isStandaloneMode}
              key={`${item?.product.name}-${item!.uid}`}
              item={item}
              locale={locale}
            />
          ))}
        </Styled.ItemList>
        <Styled.ItemListFade />
      </Styled.ItemListContainer>
      <Styled.LinkContainer>
        <ButtonOutlined fullWidth themeColor={Color.Blue_500_Ultramarine} href={`/${locale}/checkout/cart`}>
          {t('global:cart.goToCart')}
        </ButtonOutlined>
        <Button fullWidth hoverThemeColor={Color.Blue_1000_Deep} href={`/${locale}/checkout`}>
          {t('global:cart.goToCheckout')}
        </Button>
      </Styled.LinkContainer>
    </Styled.Wrapper>
  );
};

export default MiniCart;
